import {
  booleanAttribute,
  Component,
  computed,
  contentChild,
  type ElementRef,
  input,
  output,
  type TemplateRef,
  viewChild,
} from '@angular/core';
import { assertIsNotNil } from '@pct-ts/assert';
import { NgTemplateOutlet } from '@angular/common';
import { type FileUploadConfig, isSingleFileUploadConfig } from './type/file-upload-config';
import { createErrorFile, createValidFile, type SelectedFile } from './type/selected-file';
import { joinMimeTypes } from './mime-type/join-mime-types';
import { isNil } from '@pct-ts/typeguard';

@Component({
  selector: 'pct-feature-file-upload-native-component',
  imports: [NgTemplateOutlet],
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent {
  readonly nameAttribute = input.required<string>();

  readonly config = input.required<FileUploadConfig>();
  readonly disabled = input(false, { transform: booleanAttribute });

  protected readonly accept = computed(() => joinMimeTypes(this.config().accept));

  readonly filesSelect = output<SelectedFile[]>();
  readonly dragOver = output<boolean>();

  protected readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  protected readonly dropZoneContent = contentChild<TemplateRef<any>>('dropZoneContentTpl');
  protected readonly fileListContent = contentChild<TemplateRef<any>>('fileListContentTpl');

  protected onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.#emitFilesSelect(input.files);
    input.value = '';
  }

  protected onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.disabled()) {
      this.dragOver.emit(true);
    }
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Only set dragOver to false if we're leaving the drop zone itself
    // Check if the related target is outside the drop zone
    const target = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement;

    if (!target.contains(relatedTarget)) {
      this.dragOver.emit(false);
    }
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragOver.emit(false);

    if (this.disabled()) {
      return;
    }

    const files = event.dataTransfer?.files;

    if (files !== undefined && files.length > 0) {
      this.#emitFilesSelect(files);
    }
  }

  protected openFileDialog(): void {
    if (!this.disabled()) {
      const input = this.fileInput()?.nativeElement;
      assertIsNotNil(input, 'File input element not found');
      input.click();
    }
  }

  protected onClickDropZone(): void {
    this.openFileDialog();
  }

  protected onKeyupEnterDropZone(): void {
    this.openFileDialog();
  }

  protected onKeyupSpaceDropZone(): void {
    this.openFileDialog();
  }

  #validateFiles(files: File[]): SelectedFile[] {
    const config = this.config();
    const { accept, maxSize } = config;

    const results: SelectedFile[] = [];
    const filesToProcess = Array.from(files);

    for (const file of filesToProcess) {
      if (accept !== undefined && !this.#isFileTypeAccepted(file)) {
        results.push(createErrorFile(file, 'INVALID_TYPE'));
      } else if (maxSize !== undefined && file.size > maxSize) {
        results.push(createErrorFile(file, 'FILE_TOO_LARGE'));
      } else {
        results.push(createValidFile(file));
      }
    }

    if (isSingleFileUploadConfig(config)) {
      const processedResults: SelectedFile[] = [];
      let foundFirst = false;

      for (const result of results) {
        if (!foundFirst && result.isValid) {
          processedResults.push(result);
          foundFirst = true;
        } else if (!foundFirst) {
          processedResults.push(result);
        } else {
          processedResults.push(createErrorFile(result.file, 'TOO_MANY_FILES'));
        }
      }

      return processedResults;
    }
    // Miért van kikommentezve ez a rész:
    // Ez a rész felelős azért ha egyszerre több kép kerül
    // feltöltésre mint a maxFiles, akkor az első n kép feltöltődik a többi pedig törlésre kerül.
    // Az a döntés született hogy ne ezen a ponton töröljük a többi fájlt, hanem töltődjenek fel
    // a fájlok és a user törli majd a listából annyit hogy a komponenst felhasználó oldalon lévő
    // validációnak megfeleljen
    // let maxFiles = 0;
    // if (isMultipleFileUploadConfig(config)) {
    //   maxFiles = config.maxFiles ?? 0;
    // }
    // if (maxFiles > 0) {
    //   const processedResults: SelectedFile[] = [];
    //   let validFileCount = 0;
    //
    //   for (const result of results) {
    //     if (result.isValid) {
    //       if (validFileCount < maxFiles) {
    //         processedResults.push(result);
    //         validFileCount++;
    //       } else {
    //         processedResults.push(createErrorFile(result.file, 'TOO_MANY_FILES'));
    //       }
    //     } else {
    //       processedResults.push(result);
    //     }
    //   }
    //
    //   return processedResults;
    // }

    return results;
  }

  #isFileTypeAccepted(file: File): boolean {
    const acceptedMimeTypes = this.accept().split(',');

    return acceptedMimeTypes.some(acceptedType => {
      if (acceptedType.startsWith('.')) {
        // File extension check
        return file.name.toLowerCase().endsWith(acceptedType.toLowerCase());
      }
      if (acceptedType.includes('*')) {
        // MIME type wildcard check (e.g., image/*)
        const [mainType] = acceptedType.split('/');
        return file.type.startsWith(mainType);
      }
      // Exact MIME type check
      if (file.type === acceptedType) {
        return acceptedMimeTypes.includes(`.${file.name.split('.').pop()}`);
      }
      return false;
    });
  }

  #emitFilesSelect(files: FileList | File[] | null | undefined): void {
    let result: SelectedFile[] = [];
    if (Array.isArray(files)) {
      result = this.#validateFiles(files);
    } else if (!isNil(files)) {
      result = this.#validateFiles(Array.from(files));
    }
    this.filesSelect.emit(result);
  }
}
